import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Check, Loader2, QrCode, Zap, Shield, Clock, Brain, Users, Star, ArrowRight, MessageCircle, FileText, AlertTriangle, Tag, X, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const planColors = {
  demo: { border: 'border-amber-300', bg: 'bg-amber-50/50', btn: 'bg-amber-500 hover:bg-amber-600 text-white', badge: 'bg-amber-500 text-white' },
  pay_per_case: { border: 'border-stone-200', bg: 'bg-white', btn: 'bg-stone-100 text-stone-900 border border-stone-200 hover:bg-stone-200', badge: '' },
  '3_months': { border: 'border-[#1A4D2E]/30', bg: 'bg-white', btn: 'bg-[#1A4D2E] hover:bg-[#143B23] text-white', badge: '' },
  '6_months': { border: 'border-[#1A4D2E]', bg: 'bg-[#1A4D2E]/[0.02]', btn: 'bg-[#1A4D2E] hover:bg-[#143B23] text-white', badge: 'bg-[#1A4D2E] text-white' },
  '1_year': { border: 'border-[#8B2626]/30', bg: 'bg-white', btn: 'bg-[#8B2626] hover:bg-[#6B1D1D] text-white', badge: 'bg-[#8B2626] text-white' },
};

export default function SubscriptionPage() {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const [plans, setPlans] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [qrAmount, setQrAmount] = useState('');
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount_percent }
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/subscription/plans').then(r => setPlans(r.data)),
      api.get('/subscription/status').then(r => setStatus(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Auto-apply coupon from URL param (e.g. ?coupon=SAVE75 from WhatsApp links)
  useEffect(() => {
    const urlCoupon = searchParams.get('coupon');
    if (urlCoupon && !appliedCoupon && !validating) {
      const code = urlCoupon.trim().toUpperCase();
      setCouponInput(code);
      applyCoupon(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleDemoActivation = async () => {
    setPaying('demo');
    try {
      await api.post('/subscription/activate-demo');
      toast.success('Free trial activated! You have 2 free analyses.');
      const { data: newStatus } = await api.get('/subscription/status');
      setStatus(newStatus);
      await refreshUser();
      if (returnTo) navigate(returnTo);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to activate');
    } finally {
      setPaying(null);
    }
  };

  const applyCoupon = async (codeOverride) => {
    const code = (codeOverride || couponInput).trim().toUpperCase();
    if (!code) { toast.error('Enter a coupon code'); return; }
    // Validate against the most popular plan to give the user confirmation
    // (actual plan-specific check happens at payment time)
    setValidating(true);
    try {
      // Try validating against 6_months as a default - if rejected for plan,
      // we still accept the code and let the per-plan check happen at checkout.
      const res = await api.post('/coupons/validate', { code, plan: '6_months' });
      setAppliedCoupon({ code: res.data.code, discount_percent: res.data.discount_percent });
      setCouponCode(res.data.code);
      toast.success(`Coupon applied: ${res.data.discount_percent}% OFF`);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // If plan-restriction error, still accept the code (validation happens per-plan at checkout)
      if (typeof detail === 'string' && detail.includes('not valid for the selected plan')) {
        // Try other plans to find one that accepts the code
        const plans = ['3_months', '1_year', 'pay_per_case'];
        let accepted = null;
        for (const p of plans) {
          try {
            const r = await api.post('/coupons/validate', { code, plan: p });
            accepted = r.data;
            break;
          } catch (_) { /* keep trying */ }
        }
        if (accepted) {
          setAppliedCoupon({ code: accepted.code, discount_percent: accepted.discount_percent });
          setCouponCode(accepted.code);
          toast.success(`Coupon applied: ${accepted.discount_percent}% OFF (only valid for specific plans)`);
        } else {
          toast.error(detail);
        }
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Invalid coupon');
      }
    } finally {
      setValidating(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponInput('');
    toast.info('Coupon removed');
  };

  const handlePayment = async (plan) => {
    if (plan.free) { handleDemoActivation(); return; }
    setPaying(plan.id);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Payment gateway failed to load'); setPaying(null); return; }
      const orderPayload = { plan: plan.id };
      if (appliedCoupon?.code) orderPayload.coupon_code = appliedCoupon.code;
      const { data } = await api.post('/payments/create-order', orderPayload);
      const options = {
        key: data.key_id || RAZORPAY_KEY,
        amount: data.amount,
        currency: data.currency,
        name: 'ChestBuddy',
        description: data.coupon_applied ? `${plan.name} (${data.discount_percent}% OFF applied)` : plan.name,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: plan.id,
            });
            toast.success('Payment successful!');
            setAppliedCoupon(null);
            setCouponInput('');
            setCouponCode('');
            const { data: newStatus } = await api.get('/subscription/status');
            setStatus(newStatus);
            await refreshUser();
            if (returnTo) navigate(returnTo);
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || 'Verification failed');
          }
          setPaying(null);
        },
        modal: { ondismiss: () => setPaying(null) },
        theme: { color: '#1A4D2E' },
      };
      new window.Razorpay(options).open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Payment failed');
      setPaying(null);
    }
  };

  return (
    <div className="space-y-10 max-w-6xl mx-auto" data-testid="subscription-page">

      {/* ── HERO HEADER ── */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-[#1A4D2E]/10 text-[#1A4D2E] px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
          <Zap className="w-3.5 h-3.5" /> AI-Powered Diagnostics
        </div>
        <h1 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight text-stone-900 leading-tight">
          AI-Powered Chest X-ray Analysis<br />in Seconds
        </h1>
        <p className="text-stone-500 mt-3 text-base leading-relaxed">
          Get instant clinical insights, reduce uncertainty, and generate professional reports — anytime, anywhere.
        </p>
        <p className="text-sm text-[#1A4D2E] font-medium mt-2">
          Designed for busy doctors. Built for real-world OPD use.
        </p>
      </div>

      {/* ── TRUST BADGES ── */}
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
        {[
          { icon: Zap, text: 'Instant results in <10 seconds' },
          { icon: Shield, text: 'Supports clinical decisions (not replace judgment)' },
          { icon: Users, text: 'Ideal for GP clinics, emergency use & second opinions' },
        ].map((t, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-stone-600">
            <div className="w-5 h-5 rounded-full bg-[#1A4D2E]/10 flex items-center justify-center">
              <Check className="w-3 h-3 text-[#1A4D2E]" />
            </div>
            {t.text}
          </div>
        ))}
      </div>

      {returnTo && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm flex items-start gap-3 max-w-2xl mx-auto">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <span>You have no remaining credits. Choose a plan below to continue your analysis.</span>
        </div>
      )}

      {/* ── CURRENT STATUS ── */}
      {status && (
        <div className="flex items-center justify-center gap-6 py-3 px-6 bg-white border border-stone-200 rounded-xl shadow-sm max-w-lg mx-auto">
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Current Plan</p>
            <p className="text-sm font-heading font-medium text-stone-900 capitalize mt-0.5">{status.plan?.replace(/_/g, ' ') || 'None'}</p>
          </div>
          <Separator orientation="vertical" className="h-8" />
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">Credits</p>
            <p className="text-lg font-mono font-medium text-stone-900">{status.credits ?? 0}</p>
          </div>
          <Separator orientation="vertical" className="h-8" />
          <Badge className={status.is_active ? 'bg-[#1A4D2E] text-white' : 'bg-stone-200 text-stone-600'}>
            {status.is_active ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      )}

      {/* ── COUPON INPUT ── */}
      <div className="max-w-lg mx-auto">
        {appliedCoupon ? (
          <div
            className="flex items-center justify-between gap-3 px-4 py-3 bg-amber-50 border-2 border-amber-300 rounded-xl"
            data-testid="applied-coupon-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-amber-700 font-semibold uppercase tracking-wider">Coupon Applied</p>
                <p className="font-mono font-bold text-stone-900">
                  {appliedCoupon.code} <span className="text-amber-600">— {appliedCoupon.discount_percent}% OFF</span>
                </p>
              </div>
            </div>
            <Button
              variant="ghost" size="sm"
              onClick={removeCoupon}
              className="text-stone-500 hover:text-stone-900 gap-1.5"
              data-testid="remove-coupon-btn"
            >
              <X className="w-4 h-4" /> Remove
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl p-2" data-testid="coupon-input-section">
            <Tag className="w-4 h-4 text-stone-400 ml-2" />
            <Input
              value={couponInput}
              onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
              placeholder="Have a coupon code? Enter it here"
              className="border-0 shadow-none focus-visible:ring-0 font-mono uppercase placeholder:font-sans placeholder:normal-case placeholder:text-stone-400"
              maxLength={24}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCoupon(); } }}
              data-testid="coupon-input"
            />
            <Button
              onClick={applyCoupon}
              disabled={validating || !couponInput.trim()}
              size="sm"
              className="bg-[#1A4D2E] hover:bg-[#143B23] text-white shrink-0"
              data-testid="apply-coupon-btn"
            >
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        )}
        <p className="text-xs text-stone-400 text-center mt-2">
          Coupons apply only to your first paid subscription.
        </p>
      </div>

      {/* ── PRICING CARDS ── */}
      <div data-testid="subscription-plans" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {plans.map(plan => {
          const colors = planColors[plan.id] || planColors.pay_per_case;
          return (
            <Card key={plan.id} data-testid={`plan-card-${plan.id}`}
              className={`${colors.border} border-2 shadow-sm relative ${colors.bg} ${plan.popular ? 'ring-2 ring-[#1A4D2E] scale-[1.02] z-10' : ''} transition-all hover:shadow-md`}
            >
              {plan.popular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#1A4D2E] text-white px-3 py-0.5 gap-1">
                    <Star className="w-3 h-3" /> Most Chosen by Doctors
                  </Badge>
                </div>
              )}
              {plan.free && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-amber-500 text-white px-3 py-0.5">Free Trial</Badge>
                </div>
              )}

              <CardHeader className="pb-1 pt-6">
                <CardTitle className="text-base font-heading">{plan.name}</CardTitle>
                <p className="text-sm font-medium text-stone-800 mt-0.5">{plan.description}</p>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">{plan.tagline}</p>
              </CardHeader>

              <CardContent className="pb-2">
                {/* Price */}
                <div className="mb-3 mt-2">
                  {plan.free ? (
                    <span className="text-3xl font-mono font-bold text-stone-900">Free</span>
                  ) : (() => {
                    const discountedPrice = appliedCoupon
                      ? Math.round(plan.price * (1 - appliedCoupon.discount_percent / 100) * 100) / 100
                      : null;
                    return (
                      <div>
                        {discountedPrice !== null ? (
                          <div className="flex items-baseline gap-1.5 flex-wrap">
                            <span className="text-sm text-stone-400 line-through font-mono">
                              &#8377;{plan.price_display}
                            </span>
                            <span className="text-sm text-stone-500">&#8377;</span>
                            <span className="text-3xl font-mono font-bold text-amber-600">
                              {discountedPrice.toLocaleString('en-IN')}
                            </span>
                            {plan.duration ? (
                              <span className="text-xs text-stone-500 ml-1">/ {plan.duration}</span>
                            ) : (
                              <span className="text-xs text-stone-500 ml-1">/ case</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-baseline gap-0.5">
                            <span className="text-sm text-stone-500">&#8377;</span>
                            <span className="text-3xl font-mono font-bold text-stone-900">{plan.price_display}</span>
                            {plan.duration ? (
                              <span className="text-xs text-stone-500 ml-1">/ {plan.duration}</span>
                            ) : (
                              <span className="text-xs text-stone-500 ml-1">/ case</span>
                            )}
                          </div>
                        )}
                        {discountedPrice !== null && (
                          <p className="text-[10px] text-amber-700 font-semibold uppercase tracking-wider mt-1" data-testid={`discount-tag-${plan.id}`}>
                            {appliedCoupon.discount_percent}% off with {appliedCoupon.code}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Note */}
                {plan.note && (
                  <p className="text-xs text-[#1A4D2E] font-medium mb-3 flex items-center gap-1">
                    <ArrowRight className="w-3 h-3" /> {plan.note}
                  </p>
                )}

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                      <Check className="w-4 h-4 text-[#1A4D2E] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="flex-col gap-2 pt-2 pb-5">
                <Button
                  data-testid={`pay-btn-${plan.id}`}
                  onClick={() => handlePayment(plan)}
                  disabled={paying === plan.id}
                  className={`w-full rounded-lg h-10 font-medium ${colors.btn}`}
                >
                  {paying === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <span className="flex items-center gap-1.5">
                      {plan.cta || 'Subscribe'} <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  )}
                </Button>
                {!plan.free && plan.price > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => { setQrAmount(plan.price); setShowQr(true); }} className="w-full text-xs text-stone-400 hover:text-stone-600">
                    <QrCode className="w-3 h-3 mr-1" /> Pay via UPI QR
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* ── CONVERSION BOOSTERS ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Medico-Legal */}
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#1A4D2E]/10 flex items-center justify-center shrink-0">
                <Shield className="w-5 h-5 text-[#1A4D2E]" />
              </div>
              <div>
                <p className="text-sm font-heading font-semibold text-stone-900">Medico-Legal Confidence</p>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">Every report is time-stamped and documented — helping you maintain proper clinical records when needed.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Saving */}
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm font-heading font-semibold text-stone-900">Save 5-10 Minutes Per Patient</p>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">That's 1-2 extra patients every hour. More throughput, less guesswork, better outcomes.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Second Opinion */}
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                <Brain className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm font-heading font-semibold text-stone-900">Instant Second Opinion</p>
                <p className="text-xs text-stone-500 mt-1 leading-relaxed">When in doubt, get a second opinion instantly — without waiting for a radiologist callback.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── DISCLAIMER ── */}
      <div className="bg-stone-50 border border-stone-200 px-5 py-4 rounded-xl text-center">
        <p className="text-sm text-stone-600">
          <span className="font-semibold text-stone-800">Important:</span> This AI tool is designed to <strong>assist</strong> doctors and should not replace professional medical judgment. All reports are supplementary to clinical assessment.
        </p>
      </div>

      {/* ── WHATSAPP CTA ── */}
      <div className="bg-[#1A4D2E] rounded-xl px-6 py-5 text-center">
        <p className="text-white text-base font-heading font-medium mb-2">
          Want to try on your own cases?
        </p>
        <p className="text-white/70 text-sm mb-4">
          Message us on WhatsApp and get started in 2 minutes.
        </p>
        <a
          href="https://wa.me/919028776813?text=Hi%2C%20I%20want%20to%20try%20ChestBuddy%20for%20my%20clinic"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-cta"
          className="inline-flex items-center gap-2 bg-white text-[#1A4D2E] font-medium px-6 py-2.5 rounded-lg text-sm hover:bg-stone-100 transition-colors"
        >
          <MessageCircle className="w-4 h-4" />
          Chat on WhatsApp
          <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>

      {/* ── QR Dialog ── */}
      <Dialog open={showQr} onOpenChange={setShowQr}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Pay via Google Pay / UPI</DialogTitle>
            <DialogDescription>Scan this QR code with Google Pay, PhonePe, Paytm, or any UPI app.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`upi://pay?pa=abhijit.koyalikar1211@okicici&pn=ChestBuddy&am=${qrAmount}&cu=INR`)}`}
              alt="UPI QR Code"
              className="rounded-lg border border-stone-200"
            />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-stone-700">Amount: &#8377;{qrAmount?.toLocaleString()}</p>
            <p className="text-xs text-stone-400">After payment, your plan will be activated upon verification.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
